import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { camelCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { checkArray } from '../../../Components/helper';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import * as action from './store/CustomSections.action';
import * as userAction from '../../UserSetting/store/User.action';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';

function CustomSectionTableList({ searchText }) {
  const reduxData = useSelector((prevState) => prevState);
  const { t } = useTranslation('common');

  const {
    customSectionData: { customSectionList },
    Users: { logInRights },
  } = reduxData;
  const { CheckActionPermission } = useComponent();
  const rangePlanAddEditAccess = CheckActionPermission(logInRights, 'rangePlan', 'default', 'addEdit');
  const rangePlanDeleteAccess = CheckActionPermission(logInRights, 'rangePlan', 'default', 'delete');
  const dateFormat = 'DD-MMMM-YYYY HH:mm';

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('loggedInUserInfo'));
    if (user?.role === 'User') {
      const payload = {
        section: 'all',
      };
      dispatch(userAction.fetchLogInRights(payload));
    }
  }, []);

  const checkPermission = (obj) => {
    const dynamicName = logInRights?.[0]?.rights?.rangePlan?.find((rightEle) => {
      return rightEle?.label === `${obj?.name} (Custom Section)`;
    })?.module;
    return CheckActionPermission(logInRights, 'rangePlan', dynamicName, 'addEdit');
  };

  const checkDeletePermission = (obj) => {
    const dynamicName = logInRights?.[0]?.rights?.rangePlan?.find((rightEle) => {
      return rightEle?.label === `${obj?.name} (Custom Section)`;
    })?.module;
    return CheckActionPermission(logInRights, 'rangePlan', dynamicName, 'delete');
  };

  const dispatch = useDispatch();
  const [state, setState] = useState({
    listData: [],
    deleteModalState: false,
    deleteId: null,
    sortOrder: '',
    sortPropertyName: '',
    propertName: '',
    columnSortValue: 'asc',
    defaultModal: false,
    defaultId: null,
    defaultLoader: false,
  });
  const { defaultModal, defaultId, deleteModalState, listData, deleteId, propertName, columnSortValue, defaultLoader } = state;
  useEffect(() => {
    if (Array.isArray(customSectionList)) {
      let arrayList = customSectionList;
      if (searchText) {
        arrayList = arrayList.filter((val) => val.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1);
      }
      setState((prevState) => ({ ...prevState, listData: arrayList }));
    }
  }, [searchText, customSectionList]);

  const toggleDeleteModal = (boolValue, id) => {
    if (id) {
      setState((prevState) => ({
        ...prevState,
        deleteId: id,
        deleteModalState: boolValue,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        deleteId: null,
        deleteModalState: boolValue,
      }));
    }
  };
  const deleteConfirmed = async () => {
    if (deleteId) {
      const deleteSectionData = listData.filter((ele) => ele._id.toString() === deleteId);
      const sectionName = camelCase(deleteSectionData[0].name);
      const res = await dispatch(action.deleteSection(deleteId, sectionName));
      if (res) {
        toggleDeleteModal(false);
      }
    }
  };
  const onSortColumns = (key, columnSortVal) => {
    let sortedData = listData;
    if (propertName !== key) {
      columnSortVal = 'asc';
    } else if (columnSortVal === 'asc') {
      columnSortVal = 'desc';
    } else if (columnSortVal === 'desc') {
      columnSortVal = 'asc';
    }
    const isAscending = columnSortVal === 'asc';
    if (isAscending) {
      sortedData = sortedData.sort((a, b) => (a[key].toLowerCase() > b[key].toLowerCase() ? 1 : -1));
    } else {
      sortedData = sortedData.sort((a, b) => (a[key].toLowerCase() > b[key].toLowerCase() ? -1 : 1));
    }
    setState((prevState) => ({
      ...prevState,
      propertName: key,
      columnSortValue: columnSortVal,
      listData: sortedData,
    }));
  };

  const headerSortingIcon = (label, propertyName) => {
    const isActive = propertName === propertyName;
    let iconClass = 'icon-Table-Up font-weight-heavy text-black f-14 ml-1';

    if (isActive) {
      if (state?.columnSortValue === 'desc') {
        iconClass = 'icon-Table-Down font-weight-heavy text-black f-14 ml-1';
      } else {
        // covers "" or "asc" (default)
        iconClass = 'icon-Table-Up font-weight-heavy text-black f-14 ml-1';
      }
    }

    return (
      <span className="cursor-pointer">
        {label}
        <i className={iconClass} />
      </span>
    );
  };

  const setDefault = async () => {
    setState((prev) => ({
      ...prev,
      defaultLoader: true,
    }));
    await dispatch(action.setDefaultSectionAction(defaultId));
    await dispatch(action.fetchCustomSectionList());
    setState((prev) => ({
      ...prev,
      defaultId: null,
      defaultModal: false,
      defaultLoader: false,
    }));
  };
  const openDefaultModal = (id) => {
    setState((prev) => ({
      ...prev,
      defaultId: id,
      defaultModal: true,
    }));
  };
  return (
    <>
      <div className="table-responsive">
        <table className="table common-custom-table">
          <thead>
            <tr>
              <th
                onClick={() => {
                  onSortColumns('name', columnSortValue);
                }}
                className="w-300px"
              >
                {headerSortingIcon(
                  t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.TEMPLATE_NAME_LABEL'),
                  t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.NAME'),
                )}
              </th>
              <th
                className="d-none d-md-table-cell"
                onClick={() => {
                  onSortColumns('createdAt', columnSortValue);
                }}
              >
                {headerSortingIcon(
                  t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.CREATED_BY_DATE_LABEL'),
                  t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.CREATED_AT'),
                )}
              </th>
              <th
                className="d-none d-md-table-cell"
                onClick={() => {
                  onSortColumns('modifiedAt', columnSortValue);
                }}
              >
                {headerSortingIcon(
                  t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.MODIFIED_BY_DATE_LABEL'),
                  t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.MODIFIED_AT'),
                )}
              </th>
              {(rangePlanAddEditAccess || rangePlanDeleteAccess) && <th />}
            </tr>
          </thead>
          <tbody>
            {checkArray(listData) ? (
              listData.map((sec, i) => {
                return (
                  <tr key={i}>
                    <td>{sec?.name || '--'}</td>
                    <td className="d-none d-md-table-cell">
                      {`${sec?.createdBy?.firstName || ''} ${sec?.createdBy?.lastName || ''} ${sec?.createdAt && moment(sec?.createdAt).format(dateFormat)}`}
                    </td>
                    <td className="d-none d-md-table-cell">
                      {`${sec?.modifiedBy?.firstName || ''} ${sec?.modifiedBy?.lastName || ''} ${
                        sec?.modifiedAt && moment(sec?.modifiedAt).format(dateFormat)
                      }`}
                    </td>
                    {(rangePlanAddEditAccess || rangePlanDeleteAccess) && (
                      <td>
                        <div className="table-action-container">
                          {checkPermission(sec) && (
                            <Link title="Edit" className="btn p-0 d-inline-flex" to={`/rangeplan/product-list/columns/section/${sec?._id}`}>
                              <span className="icon-Edit-1 f-20" />
                            </Link>
                          )}
                          {checkDeletePermission(sec) && (
                            <button
                              type="button"
                              onClick={() => toggleDeleteModal(true, sec?._id)}
                              className="btn p-0 d-inline-flex"
                              title={t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.DELETE')}
                            >
                              <span className="icon-Delete f-20" />
                            </button>
                          )}

                          <div className="info-container d-flex align-items-center text-left d-md-none ml-0 mr-2 btn-tbl-action">
                            <span className="icon-info f-18" />
                            <div className="info-content">
                              <p className="mb-1">
                                <b>{t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.CREATED_BY')}&nbsp;</b>
                                {`${sec?.createdBy?.firstName || ''} ${sec?.createdBy?.lastName || ''} ${
                                  sec?.createdAt && moment(sec?.createdAt).format(dateFormat)
                                }`}
                              </p>
                              <p className="mb-1">
                                <b>{t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.MODIFIED_BY')}&nbsp;</b>
                                {`${sec?.modifiedBy?.firstName || ''} ${sec?.modifiedBy?.lastName || ''} ${
                                  sec?.modifiedAt && moment(sec?.modifiedAt).format(dateFormat)
                                }`}
                              </p>
                            </div>
                          </div>
                          {rangePlanAddEditAccess && (
                            <p className="custom-radio btn-tbl-action p-0 ml-16 mb-0">
                              <input type="radio" id={`default_${i}`} checked={sec?.isDefault} onClick={() => openDefaultModal(sec?._id)} name="isDefault" />
                              <label
                                className="mb-0 "
                                title={t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.SET_AS_DEFAULT_LABEL')}
                                htmlFor={`default_${i}`}
                              >
                                &nbsp;
                              </label>
                            </p>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td className="text-center" colSpan={4}>
                  {t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.NO_RECORDS_MESSAGE')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {deleteModalState && (
        <ConfirmationPopup
          onOpen={deleteModalState}
          onConfirm={() => deleteConfirmed()}
          title={t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.DELETE_SECTION_TITLE')}
          message={t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.DELETE_SECTION_MESSAGE')}
          secondMessage={t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.DELETE_SECTION_SECOND_MESSAGE')}
          toggle={() => {
            toggleDeleteModal(!deleteModalState);
          }}
          loading={false}
        />
      )}
      {defaultModal && (
        <ConfirmationPopup
          onOpen={defaultModal}
          onConfirm={() => setDefault()}
          title={t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.SET_DEFAULT_TITLE')}
          message={t('RANGE_PLANS.CUSTOM_SECTION.CUSTOM_SECTION_TABLE.SET_DEFAULT_MESSAGE')}
          toggle={() => {
            setState((prev) => ({
              ...prev,
              defaultModal: false,
              defaultId: null,
            }));
          }}
          loading={defaultLoader}
        />
      )}
    </>
  );
}

export default CustomSectionTableList;
